/**
 * authService.ts — Cognito SRP authentication service (AUTH-01/02/03)
 *
 * Implements RESEARCH Pattern 4 (amazon-cognito-identity-js 6.3.18):
 *
 *   signIn(email, password)
 *     → USER_SRP_AUTH via authenticateUser — password never sent in plaintext (T-01-23)
 *     → rejects with 'NEW_PASSWORD_REQUIRED' when AdminCreateUser temp-password must be changed
 *
 *   completeNewPassword(newPassword)
 *     → completes the NEW_PASSWORD_REQUIRED challenge (D-09 AdminCreateUser flow)
 *     → uses the module-level pendingChallengeUser stored during signIn
 *
 *   getSession()
 *     → returns the current auto-refreshed session, or null when both tokens are expired
 *       (Pitfall 2: library returns null silently — callers must redirect, never send
 *       "Bearer undefined")
 *     → returns null immediately when the Cognito pool is not configured (local dev)
 *
 *   signOut()
 *     → calls globalSignOut() which invalidates the Cognito session server-side (AUTH-03)
 *     → avoids the prototype's fetch()-to-/logout CORS error anti-pattern
 *
 * Env vars are read inside each function (not at module level) so vi.stubEnv() works
 * correctly in tests.
 */
import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserAttribute,
  AuthenticationDetails,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js'
import { getCognitoConfig } from './runtimeConfig'

// ---------------------------------------------------------------------------
// Module-level state for the NEW_PASSWORD_REQUIRED challenge flow (D-09)
// The CognitoUser instance must be reused across the two-step challenge.
// ---------------------------------------------------------------------------
let pendingChallengeUser: CognitoUser | null = null

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Read pool config fresh on each call. Resolves from runtime config (GET /api/config.json,
 * loaded at app startup) with a build-time VITE_ env fallback for local dev / tests
 * (getCognitoConfig handles the precedence; reading fresh keeps vi.stubEnv() working).
 */
function getPoolConfig() {
  const { userPoolId, clientId } = getCognitoConfig()
  return { UserPoolId: userPoolId, ClientId: clientId }
}

function isConfigured(): boolean {
  const { UserPoolId, ClientId } = getPoolConfig()
  return Boolean(UserPoolId && ClientId)
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Sign in with email and password via USER_SRP_AUTH.
 * Passwords never leave the browser in plaintext (SRP challenge is all Cognito sees).
 */
export async function signIn(email: string, password: string): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    const config = getPoolConfig()
    const userPool = new CognitoUserPool(config)
    const user = new CognitoUser({ Username: email, Pool: userPool })
    const details = new AuthenticationDetails({
      Username: email,
      Password: password,
    })

    user.authenticateUser(details, {
      onSuccess: (session) => {
        pendingChallengeUser = null
        resolve(session)
      },
      onFailure: (err) => {
        reject(err)
      },
      newPasswordRequired: () => {
        // Store user so completeNewPassword() can finish the challenge
        pendingChallengeUser = user
        reject(new Error('NEW_PASSWORD_REQUIRED'))
      },
    })
  })
}

/**
 * Complete a NEW_PASSWORD_REQUIRED challenge (D-09 AdminCreateUser flow).
 * Must be called after signIn rejects with 'NEW_PASSWORD_REQUIRED'.
 */
export async function completeNewPassword(newPassword: string): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    if (!pendingChallengeUser) {
      reject(new Error('No pending password challenge. Call signIn first.'))
      return
    }
    pendingChallengeUser.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: (session) => {
          pendingChallengeUser = null
          resolve(session)
        },
        onFailure: (err) => {
          reject(err)
        },
      },
    )
  })
}

/**
 * Return the current session (auto-refreshed via the refresh token), or null.
 *
 * Returns null when:
 *   - Cognito pool is not configured (no env vars — DEV mode, local-core)
 *   - No user is signed in
 *   - Both access token AND refresh token are expired (Pitfall 2)
 *
 * Callers must treat null as "redirect to login" — never send "Bearer undefined".
 */
export async function getSession(): Promise<CognitoUserSession | null> {
  if (!isConfigured()) return null

  const config = getPoolConfig()
  const userPool = new CognitoUserPool(config)
  const user = userPool.getCurrentUser()
  if (!user) return null

  return new Promise<CognitoUserSession | null>((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      // Resolve with null on error (expired tokens) — callers redirect to login
      resolve(err ? null : session)
    })
  })
}

/**
 * Remove the amazon-cognito-identity-js token set for a client from localStorage.
 *
 * Belt-and-suspenders for signOut (T-042): globalSignOut()'s onSuccess and the SDK's
 * local signOut() both clear these keys, but the T-034 prod UAT proved they can survive
 * a failed sign-out and leave the browser authenticated. This scoped purge guarantees the
 * `CognitoIdentityServiceProvider.<clientId>.*` keys are gone regardless of which path ran.
 */
function clearCognitoLocalStorage(clientId: string): void {
  if (typeof localStorage === 'undefined' || !clientId) return
  const prefix = `CognitoIdentityServiceProvider.${clientId}`
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith(prefix)) localStorage.removeItem(key)
  }
}

/**
 * Tell the API that this session has ended, so it can reject the access token already issued
 * (T-371 item 3 — see apps/api/src/lib/tokenRevocation.ts).
 *
 * Deliberately a bare fetch rather than lib/api.ts's authedFetch: that module imports THIS one,
 * so calling it here would close an import cycle. It also would not help — the token is already
 * in hand, and authedFetch's 401-refresh-and-retry behaviour is the opposite of what a sign-out
 * wants.
 *
 * Swallows everything. A user who pressed sign out must end up signed out whatever the network
 * did; a failure here degrades to the behaviour we had before this endpoint existed.
 */
async function revokeApiSession(session: CognitoUserSession): Promise<void> {
  try {
    await fetch('/api/auth/signout', {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.getAccessToken().getJwtToken()}` },
    })
  } catch {
    // Intentionally ignored — see above.
  }
}

/**
 * Sign out and invalidate the Cognito session server-side (AUTH-03), then always
 * terminate the local session — never leave the browser authenticated.
 *
 * Uses globalSignOut(), NOT fetch() to the Cognito /logout endpoint (the fetch approach is
 * CORS-blocked from browser JS — the prototype's bug).
 *
 * T-034/T-042: globalSignOut() needs a hydrated session (access token), but getCurrentUser()
 * returns a user WITHOUT signInUserSession — so a naive globalSignOut() rejected immediately
 * (no network call) and the old caller left the tokens in place. We now:
 *   1. hydrate via getSession() first; only then attempt the server-side revoke,
 *   2. fall back to the SDK's local signOut() when hydration or the revoke fails,
 *   3. always purge the Cognito localStorage token set.
 * This function resolves in all reachable cases so the caller is never stranded signed-in.
 */
export async function signOut(): Promise<void> {
  if (!isConfigured()) return

  const config = getPoolConfig()
  const userPool = new CognitoUserPool(config)
  const user = userPool.getCurrentUser()

  try {
    if (user) {
      const session = await new Promise<CognitoUserSession | null>((resolve) => {
        user.getSession((err: Error | null, s: CognitoUserSession | null) => {
          resolve(err ? null : s)
        })
      })

      if (session) {
        // T-371 item 3: tell OUR api the session ended, while the access token is still in hand.
        // globalSignOut below kills the refresh token and the Cognito session, but the access
        // token already issued stays cryptographically valid until it expires — and the API
        // verifies JWTs locally, so it cannot observe a Cognito-side revocation. This is the
        // only signal it gets. Best-effort, exactly like globalSignOut: a failure here leaves
        // the pre-existing behaviour (token valid until exp), never a half-signed-out browser.
        await revokeApiSession(session)

        // Best-effort server-side revoke — never reject; local clear happens in finally.
        await new Promise<void>((resolve) => {
          user.globalSignOut({
            onSuccess: () => resolve(),
            onFailure: () => resolve(),
          })
        })
      }
    }
  } finally {
    // Always terminate the local session. user.signOut() clears the SDK's cached tokens;
    // the explicit purge covers keys the SDK may leave behind (the T-034 persistence bug).
    if (user) user.signOut()
    clearCognitoLocalStorage(config.ClientId)
    pendingChallengeUser = null
  }
}

/**
 * Start a self-service password reset (Phase 2.3). Cognito emails a verification
 * code to the account's verified email (pool: AccountRecoverySetting=verified_email,
 * EmailConfiguration=COGNITO_DEFAULT) — no administrator involvement.
 *
 * Enumeration-safe: the errors that reveal account state resolve exactly like success, so a
 * caller (and therefore the UI) cannot distinguish a registered email from an unregistered
 * one. Cognito leaks state through several exceptions, not just UserNotFoundException:
 *   - UserNotFoundException     — no such account
 *   - NotAuthorizedException    — e.g. a disabled/unconfirmed account
 *   - InvalidParameterException — e.g. account has no verified email to send a code to
 * All are swallowed. The paired UI copy is intentionally generic ("If an account exists,
 * we've sent a code"). Other errors (e.g. LimitExceededException) reject so the UI surfaces them.
 */
const ENUMERATION_SAFE_RESET_ERRORS = new Set([
  'UserNotFoundException',
  'NotAuthorizedException',
  'InvalidParameterException',
])

export async function requestPasswordReset(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const userPool = new CognitoUserPool(getPoolConfig())
    const user = new CognitoUser({ Username: email, Pool: userPool })
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err: Error) => {
        if (err?.name && ENUMERATION_SAFE_RESET_ERRORS.has(err.name)) {
          resolve()
          return
        }
        reject(err)
      },
    })
  })
}

/**
 * Complete a self-service password reset with the emailed code + a new password.
 * Rejects on CodeMismatchException / ExpiredCodeException / InvalidPasswordException so
 * the UI can map each to friendly copy (see passwordPolicy.formatAuthError).
 */
export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const userPool = new CognitoUserPool(getPoolConfig())
    const user = new CognitoUser({ Username: email, Pool: userPool })
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err: Error) => reject(err),
    })
  })
}

// ---------------------------------------------------------------------------
// Self-service signup (SS1)
// ---------------------------------------------------------------------------

/**
 * Register a new account via Cognito self-signup. Cognito sends a 6-digit
 * verification code to the provided email. The caller must then collect the
 * code and pass it to confirmSignUp() to activate the account.
 */
export async function signUp(
  email: string,
  password: string,
  organization?: string,
  role?: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const userPool = new CognitoUserPool(getPoolConfig())
    const attributes = [new CognitoUserAttribute({ Name: 'email', Value: email })]
    if (organization) {
      attributes.push(
        new CognitoUserAttribute({ Name: 'custom:organization', Value: organization }),
      )
    }
    if (role) {
      attributes.push(new CognitoUserAttribute({ Name: 'custom:user_role', Value: role }))
    }
    userPool.signUp(email, password, attributes, [], (err) => {
      if (err) {
        reject(err)
        return
      }
      resolve()
    })
  })
}

/**
 * Confirm a self-service signup with the 6-digit code Cognito emailed.
 * After confirmation the account is active and the user can sign in.
 */
export async function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const userPool = new CognitoUserPool(getPoolConfig())
    const user = new CognitoUser({ Username: email, Pool: userPool })
    user.confirmRegistration(code, true, (err) => {
      if (err) {
        reject(err)
        return
      }
      resolve()
    })
  })
}

/**
 * Change the signed-in user's own password (authenticated Settings flow).
 * Resolves the current user from the pool and hydrates a valid session first
 * (changePassword requires it), then calls Cognito changePassword.
 *
 * Error signalling (finding e): a failed session hydration is normalized to a
 * SESSION_EXPIRED error so the UI can say "sign in again" — it must NOT be confused
 * with the NotAuthorizedException that Cognito raises for a wrong *current* password
 * (which comes only from the changePassword callback below).
 */
export async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  const userPool = new CognitoUserPool(getPoolConfig())
  const user = userPool.getCurrentUser()
  if (!user) {
    throw new Error('SESSION_EXPIRED')
  }

  return new Promise((resolve, reject) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        // Session hydration failed (expired/revoked) — normalize, don't leak the raw error.
        reject(new Error('SESSION_EXPIRED'))
        return
      }
      user.changePassword(oldPassword, newPassword, (changeErr) => {
        if (changeErr) {
          reject(changeErr)
          return
        }
        resolve()
      })
    })
  })
}
